import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import RightModalLayout from '../layouts/Modal/RightModalLayout';
import CreateFunnelStepTwo from '../modules/Funnels/CreateFunnelStepTwo';
import CreateFunnelStepOne from '../modules/Funnels/CreateFunnelStepOne';
import CreateFunnelStepOpened from '../modules/Funnels/CreateFunnelStepOpened';
import CreateFunnelStepClosed from '../modules/Funnels/CreateFunnelStepClosed';
import { SIDEBAR_ACTIONS } from '../../constants/actionsConstants';
import FormProviderMC from '../components/FormProviderMC';
import FunnelsModelMC from '../models/FunnelsModelMC';
import { addToDataLayer } from '../../util/gtm';

const CreateFunnel = ({ variant = 'CREATE' }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('Funnels');
  const [step, setStep] = useState('stepOne');
  const [selectedItem, setSelectedItem] = useState({});
  const [data, setData] = useState({ name: '', openSteps: [], closedSteps: [] });
  const isCreateVariant = variant === 'CREATE';

  const { funnels } = useSelector((reduxState) => reduxState.funnelsReducer);
  const { currentRetailerUser = {} } = useSelector((reduxState) => reduxState.mainReducer);
  const canManageFunnels = Boolean(currentRetailerUser?.can_manage_funnels);

  const navigateToFunnel = (funnelWebId) => {
    const currentPath = location.pathname;
    const newPath = currentPath.match(/\/funnels(\/[^/]*)?$/)
      ? currentPath.replace(/\/funnels(\/[^/]*)?$/, `/funnels/${funnelWebId}`)
      : `${currentPath}/${funnelWebId}`;

    navigate(newPath, { replace: true });
  };

  const onChange = (key, value) => {
    setData((oldData) => ({
      ...oldData,
      [key]: value
    }));
  };

  const closeModal = useCallback(() => {
    dispatch({
      type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
      payload: { componentKey: 'CreateFunnel' }
    });
  }, [dispatch]);

  const handleSubmit = useCallback(() => {
    if (!canManageFunnels) {
      showtoast(t('access_denied.title'));
      return;
    }

    const steps = [...data.openSteps, ...data.closedSteps].map((step, index) => ({
      name: step.name,
      stagnation_time: step.stagnationDays ? Number(step.stagnationDays) : null,
      probability: parseFloat(step.probability ?? 1),
      type_step: step.qualification ?? 'step',
      position: index,
      reasons_attributes: step.reasons?.map((r) => ({ reason: r.reason }))
    }));

    const payload = {
      funnel: {
        name: data.name.trim(),
        funnel_steps_attributes: steps
      }
    };

    FunnelsModelMC.create(payload)
      .then((res) => {
        if (res.errors && res.errors.length > 0) {
          console.error('Error response:', res.errors);
          showtoast(t('createModal.error'));
          return;
        }
        addToDataLayer({ event: 'funnels_create_newFunnel', method: 'create' });
        const newFunnels = [...funnels, res];
        dispatch({ type: 'SET_FUNNELS', data: { funnels: newFunnels } });
        navigateToFunnel(res.web_id);
        closeModal();
        showtoast(t('createModal.success'));
      })
      .catch((err) => {
        console.error(err);
        showtoast(err.message || t('createModal.error'));
      });
  }, [canManageFunnels, data, funnels, t]);

  const Content = useMemo(() => {
    switch (step) {
      case 'stepOne':
        return CreateFunnelStepOne;
      case 'stepTwo':
        return CreateFunnelStepTwo;
      case 'stageOpen':
        return CreateFunnelStepOpened;
      case 'stageClosed':
        return CreateFunnelStepClosed;
      default:
        return () => { };
    }
  }, [step]);

  const { submitDisabled, submitTooltip } = useMemo(() => {
    if (step === 'stepOne' && !data.name) {
      return { submitDisabled: true, submitTooltip: t('sidebar.createFromZero.errors.name') };
    }
    if (step === 'stepTwo' && data.closedSteps.length === 0 && data.openSteps.length === 0) {
      return { submitDisabled: true, submitTooltip: t('sidebar.createFromZero.errors.steps') };
    }
    return { submitDisabled: false };
  }, [step, data.name, data.closedSteps, data.openSteps]);

  const closeAction = () => {
    setStep('stepTwo');
    setSelectedItem({});
  };

  const methods = useForm();

  const dataStep = useMemo(() => {
    switch (step) {
      case 'stepOne':
        return {
          title: t('sidebar.createFromZero.stepOne.title'),
          submitLabel: t('sidebar.createFromZero.stepOne.submitLabel'),
          cancelLabel: t('sidebar.createFromZero.stepOne.cancelLabel'),
          caption: t('sidebar.createFromZero.caption'),
          handleSubmit: () => setStep('stepTwo'),
          cancelAction: closeModal
        };
      case 'stepTwo':
        return {
          title: t('sidebar.createFromZero.stepTwo.title'),
          submitLabel: isCreateVariant
            ? t('sidebar.createFromZero.stepTwo.submitLabel')
            : t('sidebar.createFromZero.stepTwo.submitLabelUpdate'),
          cancelLabel: t('sidebar.createFromZero.stepTwo.cancelLabel'),
          caption: isCreateVariant
            ? t('sidebar.createFromZero.caption')
            : t('sidebar.createFromZero.captionUpdate'),
          cancelAction: () => setStep('stepOne'),
          handleSubmit: methods.handleSubmit(handleSubmit)
        };
      case 'stageOpen':
        return {
          title: selectedItem.id
            ? t('sidebar.createFromZero.stageOpen.titleEdit')
            : t('sidebar.createFromZero.stageOpen.titleNew'),
          showBackButton: true,
          closeAction
        };
      case 'stageClosed':
        return {
          title: selectedItem.id
            ? t('sidebar.createFromZero.stageClosed.titleEdit')
            : t('sidebar.createFromZero.stageClosed.titleNew'),
          showBackButton: true,
          closeAction
        };
      default:
        return {};
    }
  }, [step, selectedItem, handleSubmit]);

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={dataStep.title}
        submitAction={dataStep.handleSubmit}
        cancelAction={dataStep.cancelAction}
        closeAction={dataStep.closeAction}
        showBackButton={dataStep.showBackButton}
        submitLabel={dataStep.submitLabel}
        cancelLabel={dataStep.cancelLabel}
        submitDisabled={submitDisabled}
        caption={dataStep.caption}
        submitTooltip={submitTooltip}
      >
        <FormProviderMC methods={methods}>
          <Content
            data={data}
            onChange={onChange}
            setStep={setStep}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            closeAction={closeAction}
          />
        </FormProviderMC>
      </RightModalLayout>
    </div>
  );
};

export default CreateFunnel;
